// exclusionBasisLoader — 「人を外して戻す」の G 判定に要る入力を HTTP で集める
// (exclusion-ui-investigation-2026-10-10.md「③-a」)。
//
//   - leaf セッション: 過去キャンバス行ならその行の sessionId (一覧から)、現行文書なら
//     世代一覧 API (`sessionId` 省略) が返す現行セッション
//   - baked: 一覧の `kind: 'excluded'` 行のセッション (サーバ `listExcluded` と同じ)
//   - 系譜の各セッションの世代一覧 (`?sessionId=`)。許可集合は現行 ∪ この部屋の過去キャンバス行
//
// どれか 1 つでも取れなければ null (= G を出さない。確定の可否はサーバが正)。
// 純関数の組み立ては `application/exclusionBase.ts`。

import type { LineageDoc, StrokeLogEvent } from '@oekaki/protocol';
import { lineageSessionIds, resolveWindowedLineage } from '../../application/exclusionBase.js';
import { fetchPastCanvasList } from './pastCanvasClient.js';
import { fetchRecoveryGenerations } from './recoveryGenerationsClient.js';

export interface ExclusionBasis {
  readonly docs: readonly LineageDoc[];
  readonly generations: ReadonlyMap<
    string,
    ReadonlyArray<{ readonly seq: number; readonly savedAt: string }>
  >;
}

export interface ExclusionBasisPrefetch {
  /** 過去キャンバス一覧 (baked / 行の sessionId)。取れなければ null。 */
  readonly list: Awaited<ReturnType<typeof fetchPastCanvasList>>;
  /** 現行文書のときだけ: 現行セッションの世代一覧。 */
  readonly current: Awaited<ReturnType<typeof fetchRecoveryGenerations>> | null;
}

interface CommonInput {
  readonly dispatcherUrl: string;
  readonly roomId: string;
  readonly adminToken?: string;
  readonly signal?: AbortSignal;
}

/** 絵ログの読み込みと並行して先に取れるもの。 */
export const prefetchExclusionBasis = async (
  input: CommonInput & { readonly pastCanvasId: string | undefined },
): Promise<ExclusionBasisPrefetch> => {
  const common = {
    dispatcherUrl: input.dispatcherUrl,
    roomId: input.roomId,
    ...(input.adminToken !== undefined ? { adminToken: input.adminToken } : {}),
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  };
  // 現行文書は sessionId 省略で世代一覧を取る (この応答が現行セッションを教える)。
  const [list, current] = await Promise.all([
    fetchPastCanvasList(common),
    input.pastCanvasId === undefined ? fetchRecoveryGenerations(common) : Promise.resolve(null),
  ]);
  return { list, current };
};

export interface ExclusionLineageResolution {
  /** 系譜が解けなかった / 世代一覧が取れなかった = null (G は出さない)。 */
  readonly basis: ExclusionBasis | null;
  /**
   * 系譜の根まで読めていない (窓の下端が仮の根)。世代一覧が取れなくても分かる
   * (leaf セッションが分からないときは世代の分類に影響しない仮の値で系譜の形だけ解く)。
   */
  readonly truncated: boolean;
}

export interface ExclusionBasisSource {
  /** baked 行 (`kind: 'excluded'`) のセッション。文書範囲 (系譜) をここで止める。 */
  readonly bakedSessionIds: ReadonlySet<string>;
  /**
   * 読み込んだ窓から系譜を解き、系譜の各セッションの世代一覧を足す。世代一覧はセッションごとに
   * 1 回だけ取る (窓を広げて系譜が変わっても、新しく出たセッションの分だけ追加で取る)。
   */
  resolve(window: {
    readonly events: readonly StrokeLogEvent[];
    readonly hasOlder: boolean;
    readonly floorSeq: number | null;
  }): Promise<ExclusionLineageResolution>;
}

/** 絵ログが読めた後: 窓ごとに系譜を解いて、系譜の各セッションの世代一覧を取る。 */
export const createExclusionBasisSource = (
  input: CommonInput & {
    readonly pastCanvasId: string | undefined;
    readonly rowEndSeq: number | undefined;
    readonly prefetch: ExclusionBasisPrefetch;
  },
): ExclusionBasisSource => {
  const { list, current } = input.prefetch;
  let leafSessionId: string | null = null;
  if (list.outcome === 'ok') {
    if (input.pastCanvasId !== undefined) {
      leafSessionId = list.items.find((i) => i.id === input.pastCanvasId)?.sessionId ?? null;
    } else {
      leafSessionId = current?.outcome === 'ok' ? current.sessionId : null;
    }
  }
  const bakedSessionIds = new Set(
    list.outcome === 'ok'
      ? list.items.filter((i) => i.kind === 'excluded').map((i) => i.sessionId)
      : [],
  );
  const common = {
    dispatcherUrl: input.dispatcherUrl,
    roomId: input.roomId,
    ...(input.adminToken !== undefined ? { adminToken: input.adminToken } : {}),
    ...(input.signal !== undefined ? { signal: input.signal } : {}),
  };
  const cache = new Map<string, Promise<Awaited<ReturnType<typeof fetchRecoveryGenerations>>>>();
  const generationsOf = (sessionId: string) => {
    let p = cache.get(sessionId);
    if (p === undefined) {
      p =
        current?.outcome === 'ok' && current.sessionId === sessionId
          ? Promise.resolve(current)
          : fetchRecoveryGenerations({ ...common, sessionId });
      cache.set(sessionId, p);
    }
    return p;
  };

  return {
    bakedSessionIds,
    resolve: async (window) => {
      const lineageInput = {
        events: window.events,
        ...(input.rowEndSeq !== undefined ? { rowEndSeq: input.rowEndSeq } : {}),
        leafSessionId: leafSessionId ?? '',
        bakedSessionIds,
        hasOlder: window.hasOlder,
        floorSeq: window.floorSeq,
      };
      const lineage = resolveWindowedLineage(lineageInput);
      if (lineage === null) return { basis: null, truncated: window.hasOlder };
      if (leafSessionId === null) return { basis: null, truncated: lineage.truncated };
      const sessions = lineageSessionIds(lineage.docs);
      const results = await Promise.all(sessions.map(generationsOf));
      const generations = new Map<string, ReadonlyArray<{ seq: number; savedAt: string }>>();
      for (let i = 0; i < sessions.length; i++) {
        const r = results[i];
        if (r === undefined || r.outcome !== 'ok') {
          return { basis: null, truncated: lineage.truncated };
        }
        generations.set(
          sessions[i] as string,
          r.generations.map((g) => ({ seq: g.seq, savedAt: g.savedAt })),
        );
      }
      return { basis: { docs: lineage.docs, generations }, truncated: lineage.truncated };
    },
  };
};
